import NotaPrevizualizare from '../components/NotaPrevizualizare';
import PublicPagination from '../components/PublicPagination';
import {Box, Typography} from '@mui/material';
import DescriereFormatata from '../components/DescriereFormatata';
import VitrinaLucrari from '../components/VitrinaLucrari';
import React, {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import axios from "axios";
import {API_BASE, API_ENDPOINT} from "../config";
import GrilaLucrari from "../components/GrilaLucrari";
import {lucrarePath, membruPath} from "../utils/urlPublic";
import {hideSplash} from "../splash";
import {TitluCuDistribuie} from "../components/DistribuieButton";
import LikeButton, {ActiuniCard} from "../components/LikeButton";
import {useAprecieriMele} from "../utils/aprecieri";

// „Toate lucrarile ↓” din vitrina si paginarea (pasul 47): derulare la grila (fara ancora in URL — merge si cu
// HashRouter in Cordova).
const laLucrari = () => document.getElementById('lucrari-membru')?.scrollIntoView({behavior: 'smooth', block: 'start'});

// Poza membrului (prima), flotata in stanga CV-ului: textul pleaca de langa ea si curge dedesubt.
const PozaMembru = ({poza, nume}) => {
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [poza]);
    if (!poza || failed) return null;
    const src = /^https?:\/\//i.test(poza) ? poza : `${API_BASE.replace(/\/$/, '')}/${poza.replace(/^\/+/, '')}`;
    return (
        <Box component="img" src={src} alt={nume} onError={() => setFailed(true)}
             sx={{float: 'left', width: {xs: 120, sm: 180, md: 220}, height: 'auto', mr: {xs: 2, sm: 3}, mb: 2,
                  borderRadius: 1, background: 'var(--gallery-paper)'}} />
    );
};

// ADR-0001: `type` vine din ruta (/artisti sau /colectionari). 31.7: adresa veche (slug din istoric, alt tip)
// -> API-ul da membrul curent; trecem pe adresa lui canonica (replace).
const ArtistPublicPage = ({type = 'artist'}) => {
    const {membru} = useParams();
    const navigate = useNavigate();
    const {search} = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const pagina = Math.max(1, parseInt(searchParams.get('pagina') || '1', 10) || 1);

    const [artist, setArtist] = useState(null);
    const [loading, setLoading] = useState(true);
    // Ecranul de încărcare (src/splash.js) dispare când pagina are datele - fără „pâlpâire” la deschidere.
    useEffect(() => {
        if (!loading) hideSplash();
    }, [loading]);
    const [notFound, setNotFound] = useState(false);
    // 54.5: starea „apreciat de mine” pentru membru si lucrarile din pagina curenta, intr-o singura cerere.
    const apreciat = useAprecieriMele({
        membri: artist?.id ? [artist.id] : [],
        lucrari: (artist?.lucrari || []).filter((l) => l.likes !== null).map((l) => l.id),
    });

    useEffect(() => {
        const controller = new AbortController();
        let mutat = false; // trecem pe adresa canonica: ramane „Se incarca” pana la cererea de acolo
        setLoading(true);
        setNotFound(false);
        // Pasul 47: la schimbarea paginii (acelasi membru) continutul ramane vizibil pana vin datele noi.
        setArtist((a) => (a && a.slug === membru && a.type === type ? a : null));
        axios.get(`${API_ENDPOINT}/public/membri/${membru}`, {params: {pagina}, signal: controller.signal})
            .then((r) => {
                if (r.data.type === type && r.data.slug === membru) setArtist(r.data);
                else { mutat = true; navigate(`${membruPath(r.data.type, r.data.slug)}${search}`, {replace: true}); }
            })
            .catch(() => { if (!controller.signal.aborted) setNotFound(true); })
            .finally(() => { if (!controller.signal.aborted && !mutat) setLoading(false); });
        return () => controller.abort();
    }, [membru, pagina, type, navigate, search]);

    // Pasul 47: paginarea nu urca la inceputul paginii (state.pastreazaScroll, vezi useRestaurareScroll);
    // dupa ce vin lucrarile paginii cerute (nu ale celei vechi, inca afisate), derulam la titlul „Lucrări”.
    const paginaDeDerulat = useRef(null);
    const schimbaPagina = (p) => {
        paginaDeDerulat.current = p;
        setSearchParams({pagina: p}, {state: {pastreazaScroll: true}});
    };
    useEffect(() => {
        if (!loading && paginaDeDerulat.current !== null && artist?.paginare?.pagina === paginaDeDerulat.current) {
            paginaDeDerulat.current = null;
            laLucrari();
        }
    }, [loading, artist]);

    return (
        <Box>
            <Box className="public-detail">
                {loading && !artist ? (
                    <Typography color="text.secondary">Se încarcă...</Typography>
                ) : notFound ? (
                    <Typography color="text.secondary">{type === 'colectionar' ? 'Colecționar negăsit.' : 'Artist negăsit.'}</Typography>
                ) : (
                    <>
                        <TitluCuDistribuie titlu={`${artist.nume} — ArtaNFT`} text={artist.nume}
                            like={<LikeButton tip="membri" id={artist.id} likes={artist.likes} apreciat={apreciat('membri', artist.id)} nume={artist.nume} />}>
                            <Typography component="h1" variant="h4">{artist.nume}</Typography>
                        </TitluCuDistribuie>
                        {/* flow-root: cuprinde poza flotata, ca „Lucrări” sa inceapa sub ea si la CV scurt */}
                        {/* listele devin BFC: stau langa poza, cu marcajele vizibile (nu sub ea) */}
                        <Box sx={{display: 'flow-root', mb: 2, '& ul, & ol': {overflow: 'hidden'}}}>
                            <PozaMembru poza={artist.poza} nume={artist.nume} />
                            {artist.shortCv && (
                                <Box sx={{color: 'text.secondary', '& .descriere-formatata': {mb: '1.5rem'}}}>
                                    <DescriereFormatata text={artist.shortCv} />
                                </Box>
                            )}
                            <VitrinaLucrari lucrari={artist.lucrariAleatorii} expozitieViitoare={artist.expozitieViitoare}
                                nume={artist.nume} linkLucrare={(l) => lucrarePath(type, membru, l.slug)}
                                total={artist.numarLucrari} onToate={laLucrari} afiseazaNota={false} />
                            {artist.longCv && <DescriereFormatata text={artist.longCv} />}
                        </Box>

                        <Typography component="h2" variant="h6" id="lucrari-membru" sx={{mb: 2, scrollMarginTop: 72}}>Lucrări</Typography>
                        {[...(artist.lucrari || []), ...(artist.lucrariAleatorii || [])].some((l) => l.nivel === 'teaser') && (
                            <NotaPrevizualizare contextArtist expozitie={artist.expozitieViitoare}
                                cuDetaliu={[...(artist.lucrari || []), ...(artist.lucrariAleatorii || [])].some((l) => l.nivel === 'teaser' && l.detaliu?.poza)} />
                        )}
                        <GrilaLucrari key={`${membru}-${pagina}`} lucrari={artist.lucrari} linkLucrare={(l) => lucrarePath(type, membru, l.slug)}
                            gol="Nicio lucrare adăugată încă." titluZoom={artist.nume}
                            actiuni={(l) => <ActiuniCard compact tip="lucrari" item={l} nume={l.nume} sx={{mx: -.75}}
                                cale={lucrarePath(type, membru, l.slug)} text={[l.nume, artist.nume].filter(Boolean).join(' — ')}
                                apreciat={apreciat('lucrari', l.id)} />} />
                        {artist.lucrari.length > 0 && <Box sx={{mt: 2}}>
                            <PublicPagination pagina={pagina} paginare={artist.paginare} onChange={schimbaPagina} />
                        </Box>}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ArtistPublicPage;
